/* ==========================================================================
   AndroonBazar — predictive-search.js
   Uses Shopify's native predictive search API. Keyboard accessible:
   ArrowDown / ArrowUp move through results, Escape returns to the input.
   ========================================================================== */

(function () {
  'use strict';

  const AB = window.AndroonBazar || {};
  const RECENT_KEY = 'ab:recent-searches';

  class ABPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.defaults = this.querySelector('[data-predictive-default]');
      if (!this.input || !this.results) return;

      this.controller = null;
      this.cache = new Map();

      this.onInput = (AB.debounce || debounce)(this.search.bind(this), 260);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.results.addEventListener('keydown', this.onKeydown.bind(this));

      this.closest('form') &&
        this.closest('form').addEventListener('submit', () => this.remember(this.input.value));

      this.renderRecent();
    }

    get term() {
      return this.input.value.trim();
    }

    /* --- Fetch ----------------------------------------------------------- */

    async search() {
      const term = this.term;

      if (term.length < 2) {
        this.clear();
        return;
      }

      if (this.cache.has(term)) {
        this.render(this.cache.get(term), term);
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,page,article',
        'resources[limit]': '4',
        'resources[options][unavailable_products]': 'last'
      });

      this.results.setAttribute('aria-busy', 'true');

      try {
        const res = await fetch(`${base}.json?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error('Search request failed');
        const data = await res.json();
        const resources = data.resources.results;
        this.cache.set(term, resources);
        this.render(resources, term);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.results.innerHTML = `<p class="predictive__empty">${
          (window.themeStrings && window.themeStrings.searchError) || 'Search is unavailable right now.'
        }</p>`;
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }

    /* --- Render ---------------------------------------------------------- */

    render(resources, term) {
      const products = resources.products || [];
      const collections = resources.collections || [];
      const pages = resources.pages || [];
      const articles = resources.articles || [];

      if (!products.length && !collections.length && !pages.length && !articles.length) {
        this.results.innerHTML = `<p class="predictive__empty">${escapeHtml(
          `No matches for “${term}”. Try a shorter word.`
        )}</p>`;
        this.toggleDefaults(false);
        this.input.setAttribute('aria-expanded', 'true');
        return;
      }

      let html = '<div class="predictive">';

      if (products.length) {
        html += '<div class="predictive__group"><span class="eyebrow">Products</span><div class="predictive__products">';
        products.forEach((product) => {
          const image = product.featured_image && product.featured_image.url;
          html += `
            <a class="predictive__product" href="${product.url}">
              <span class="media media--portrait">
                ${
                  image
                    ? `<img src="${image}&width=300" alt="${escapeHtml(
                        product.featured_image.alt || product.title
                      )}" width="300" height="375" loading="lazy">`
                    : ''
                }
              </span>
              <span class="predictive__product-title">${escapeHtml(product.title)}</span>
              <span class="predictive__product-price">${product.price}</span>
            </a>`;
        });
        html += '</div>';
        html += `<a class="btn btn--text predictive__all" href="/search?q=${encodeURIComponent(
          term
        )}">View all results</a>`;
        html += '</div>';
      }

      const side = [];
      if (collections.length) side.push(group('Collections', collections));
      if (pages.length) side.push(group('Pages', pages));
      if (articles.length) side.push(group('Journal', articles));
      if (side.length) html += `<div class="predictive__group">${side.join('')}</div>`;

      html += '</div>';

      this.results.innerHTML = html;
      this.toggleDefaults(false);
      this.input.setAttribute('aria-expanded', 'true');
    }

    clear() {
      this.results.innerHTML = '';
      this.toggleDefaults(true);
      this.input.setAttribute('aria-expanded', 'false');
    }

    toggleDefaults(show) {
      if (this.defaults) this.defaults.hidden = !show;
    }

    /* --- Recent searches -------------------------------------------------- */

    remember(term) {
      term = (term || '').trim();
      if (term.length < 2) return;
      try {
        const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').filter((t) => t !== term);
        list.unshift(term);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));
      } catch (e) {
        /* Private browsing — recent searches simply won't persist. */
      }
    }

    renderRecent() {
      if (!this.defaults) return;
      let list = [];
      try {
        list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      } catch (e) {
        return;
      }
      if (!list.length) return;

      const ul = document.createElement('ul');
      list.forEach((term) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'chip';
        a.href = `/search?q=${encodeURIComponent(term)}`;
        a.textContent = term;
        li.appendChild(a);
        ul.appendChild(li);
      });

      const wrap = document.createElement('div');
      const label = document.createElement('span');
      label.className = 'eyebrow';
      label.textContent = 'Recent searches';
      wrap.appendChild(label);
      wrap.appendChild(ul);
      this.defaults.prepend(wrap);
    }

    /* --- Keyboard --------------------------------------------------------- */

    onKeydown(e) {
      if (!['ArrowDown', 'ArrowUp', 'Escape'].includes(e.key)) return;

      const items = Array.from(this.results.querySelectorAll('a'));
      if (e.key === 'Escape') {
        this.input.focus();
        return;
      }
      if (!items.length) return;

      e.preventDefault();
      const current = items.indexOf(document.activeElement);
      let next;

      if (e.key === 'ArrowDown') next = current < 0 ? 0 : Math.min(current + 1, items.length - 1);
      else next = current <= 0 ? -1 : current - 1;

      if (next < 0) this.input.focus();
      else items[next].focus();
    }
  }

  /* --- Helpers ------------------------------------------------------------ */

  function group(title, items) {
    const links = items
      .map((item) => `<a href="${item.url}">${escapeHtml(item.title)}</a>`)
      .join('');
    return `<div class="predictive__group"><span class="eyebrow">${title}</span><div class="predictive__links">${links}</div></div>`;
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  }

  if (!customElements.get('ab-predictive-search')) {
    customElements.define('ab-predictive-search', ABPredictiveSearch);
  }
})();
